import { Core } from "@faire/design-tokens";
import { useFilterStore } from "@faire/retailer-visitor-shared/components/Filters/useFilterStore";
import { handleKeyDown } from "@faire/retailer-visitor-shared/lib/handleKeyDown";
import { useTopOfPageContent } from "@faire/retailer-visitor-shared/stores/ui/useTopOfPageContent";
import { useIsLoggedOutDiscoverPage } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useIsLoggedOutDiscoverPage";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Pill } from "@faire/web/slate/Filter/styled";
import { Chevron } from "@faire/web/slate/icons/Chevron";
import { Filter as FilterIcon } from "@faire/web/slate/icons/foundation/Filter";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { trackLoggedOutRetailersDiscoverPageAllFiltersPillClick } from "@faire/web-api--source/events/loggedOutRetailers/click/discoverPageAllFiltersPill";
import * as React from "react";
import styled, { css } from "styled-components";

// toggling sidebar will cause layout shifts, we want to wait 1s for it
// during which we prevent the top nav from hiding:
const TIME_TO_WAIT_FOR_LAYOUT_SHIFTS = 1000;

export const ToggleSidebarFiltersPill = () => {
  const {
    data: { activeFilterCount, isSideBarVisible },
    setFilterState,
  } = useFilterStore(["activeFilterCount", "isSideBarVisible"]);
  const { setShouldKeepTopNavVisible } = useTopOfPageContent();
  const { strictLocalize } = useStrictLocalization();
  const isLoggedOutDiscoverPage = useIsLoggedOutDiscoverPage();

  const hasFilterSelected = activeFilterCount > 0;
  const showAppliedState = isSideBarVisible || hasFilterSelected;
  const textColor = showAppliedState ? Core.text.inverse : Core.text.default;
  const iconColor = showAppliedState ? Core.icon.inverse : Core.icon.default;

  const onToggleClick = () => {
    if (isLoggedOutDiscoverPage) {
      trackLoggedOutRetailersDiscoverPageAllFiltersPillClick(
        isSideBarVisible ? "close_filters" : "open_filters"
      );
    }
    setShouldKeepTopNavVisible(true);
    setFilterState((currentState) => ({
      ...currentState,
      isSideBarVisible: !isSideBarVisible,
      isNewFilterSelected: false,
    }));
    setTimeout(() => {
      setShouldKeepTopNavVisible(false);
    }, TIME_TO_WAIT_FOR_LAYOUT_SHIFTS);
  };

  if (activeFilterCount < 0) {
    return null;
  }

  return (
    <StyledRow>
      <StyledPill
        isOpen={false}
        alwaysShowCloseButton={false}
        hasFilterSelected={isSideBarVisible}
        onClick={onToggleClick}
        onKeyDown={handleKeyDown(onToggleClick)}
        role="button"
        tabIndex={0}
        aria-label={
          !isSideBarVisible
            ? strictLocalize({
                defaultMessage: "Open filters sidebar",
                description: {
                  text: "accessible text to open the filters sidebar",
                  img: "https://cdn.faire.com/fastly/71113aed4699c6bdf36bd80d4f8840a5ecc4cfcdf3107aa4e06b89b24fc4e65f.png",
                },
              })
            : strictLocalize({
                defaultMessage: "Close filters sidebar",
                description: {
                  text: "accessible text to close the filters sidebar",
                  img: "https://cdn.faire.com/fastly/71113aed4699c6bdf36bd80d4f8840a5ecc4cfcdf3107aa4e06b89b24fc4e65f.png",
                },
              })
        }
        $showAppliedState={showAppliedState}
      >
        {isSideBarVisible ? (
          <Chevron direction="left" color={iconColor} />
        ) : null}
        <Spacer width={4} />
        {isSideBarVisible ? (
          <StyledTypography color={textColor}>
            <StrictLocalMsg
              defaultMessage="Hide filters"
              description={{
                text: "Button to hide the sidebar filters",
                img: "https://cdn.faire.com/fastly/a448df0be982953d579230433011072154f1c62548b4d7b255ad1c94d407d507.png",
              }}
            />
          </StyledTypography>
        ) : (
          <StyledTypography color={textColor}>
            <StrictLocalMsg
              defaultMessage="All filters"
              description={{
                text: "Button to open the sidebar filters",
                img: "https://cdn.faire.com/fastly/71113aed4699c6bdf36bd80d4f8840a5ecc4cfcdf3107aa4e06b89b24fc4e65f.png",
              }}
            />
            {hasFilterSelected ? ` (${activeFilterCount})` : null}
          </StyledTypography>
        )}
        <Spacer width={4} />
        {!isSideBarVisible ? (
          <FilterIcon fontSize="16px" color={iconColor} />
        ) : null}
      </StyledPill>
    </StyledRow>
  );
};

const StyledRow = styled(Row)`
  min-width: max-content;
  padding: ${getSpacing("2x", "1x", "1x", 0)};
`;

const StyledPill = styled(Pill)<{
  $showAppliedState: boolean;
}>`
  padding: ${getSpacing(0, 10)};

  ${({ $showAppliedState }) =>
    $showAppliedState
      ? css`
          color: ${Core.text.inverse};
          background-color: ${Core.surface.default};
        `
      : css`
          border: 1px solid ${Core.border.muted};
          background-color: ${Core.surface.inverse};
        `};

  &:hover {
    background-color: ${({ $showAppliedState }) =>
      $showAppliedState ? Core.surface.default : Core.surface.inverse};
    border-color: ${Core.border.default};
  }
`;

const StyledTypography = styled(Typography)`
  width: max-content;
`;
